import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react'
import { useIndexedDB } from '../hooks/useIndexedDB'
import { useSearch } from '../hooks/useSearch'
import { useToast } from '../hooks/useToast'
import { useGistWebSocket } from '../hooks/useGistWebSocket'
import ToastContainer from '../components/ToastContainer'

const GistContext = createContext()

export function GistProvider({ children }) {
  const { data: storageData, isLoading: isStorageLoading, saveToStorage } = useIndexedDB()
  const [gists, setGists] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [initialLoadComplete, setInitialLoadComplete] = useState(false)
  const { searchIndex, searchResults, search, clearSearch } = useSearch(gists)
  const toast = useToast()
  const gistsRef = useRef(gists)

  const handleGistUpdate = useCallback((gist) => {
    if (gist._deleted) {
      setGists(prev => prev.filter(g => g.gist_id !== gist.gist_id))
      return
    }

    setGists(prev => {
      const existingIndex = prev.findIndex(g => g.gist_id === gist.gist_id)

      if (existingIndex >= 0) {
        const existing = prev[existingIndex]
        const existingTime = new Date(existing.gist_updated_at).getTime()
        const newTime = new Date(gist.gist_updated_at).getTime()

        if (existingTime === newTime) {
          return prev
        }

        const updated = [...prev]
        updated[existingIndex] = gist
        return updated.sort((a, b) =>
          new Date(b.gist_created_at) - new Date(a.gist_created_at)
        )
      } else {
        const updated = [...prev, gist]
        return updated.sort((a, b) =>
          new Date(b.gist_created_at) - new Date(a.gist_created_at)
        )
      }
    })
  }, [])

  const handleHistoryComplete = useCallback((data) => {
    if (data.count > 0) {
      toast.success(`Synchronized ${data.count} gist(s)`)
    }
  }, [toast])

  const handleUnauthorized = useCallback(() => {
    toast.error('Session expired. Please log in again.')
    setTimeout(() => {
      window.location.href = '/'
    }, 1000)
  }, [toast])

  const { isConnected, reconnect } = useGistWebSocket({
    gists,
    initialLoadComplete,
    onGistUpdate: handleGistUpdate,
    onHistoryComplete: handleHistoryComplete,
    onUnauthorized: handleUnauthorized
  })

  const forceResync = useCallback(() => {
    setGists([])
    saveToStorage([])
    toast.success('Resyncing all gists...')
    reconnect()
  }, [saveToStorage, toast, reconnect])

  useEffect(() => {
    if (!isStorageLoading && storageData) {
      if (storageData.gists && storageData.gists.length > 0) {
        const sorted = [...storageData.gists].sort((a, b) =>
          new Date(b.gist_created_at) - new Date(a.gist_created_at)
        )
        setGists(sorted)
      }
      setInitialLoadComplete(true)
      setIsLoading(false)
    }
  }, [isStorageLoading])

  useEffect(() => {
    gistsRef.current = gists
  }, [gists])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      saveToStorage(gists)
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [gists, saveToStorage])

  useEffect(() => {
    const handleBeforeUnload = () => {
      saveToStorage(gistsRef.current)
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [saveToStorage])


  const handleSync = async () => {
    setError(null)

    try {
      const gistList = gists.map(g => ({
        gist_id: g.gist_id,
        updated_at: g.gist_updated_at
      }))

      const response = await fetch('/api/gists/sync', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ gists: gistList })
      })

      if (response.status === 401 || response.status === 403) {
        handleUnauthorized()
        return
      }

      if (!response.ok) {
        throw new Error(`Sync request failed: ${response.status}`)
      }

      toast.success('Synchronization scheduled')
    } catch (err) {
      console.error('Sync error:', err)
      setError(err.message)
      toast.error('Failed to trigger sync')
    }
  }

  return (
    <GistContext.Provider
      value={{
        gists,
        isLoading,
        error,
        syncGists: handleSync,
        forceResync,
        searchIndex,
        searchResults,
        search,
        clearSearch,
        toast,
        isConnected,
        reconnect,
      }}
    >
      {children}
      <ToastContainer toasts={toast.toasts} onClose={toast.hideToast} />
    </GistContext.Provider>
  )
}

export function useGists() {
  const context = useContext(GistContext)
  if (!context) {
    throw new Error('useGists must be used within GistProvider')
  }
  return context
}
